import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import transactionActions from "../../../redux/transactions/actions";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { Select, Space, Spin, Row, Col, Button } from "antd";
import "./index.scss";
import { DownloadOutlined } from "@ant-design/icons";
const { Option } = Select;
const { getTransactionsData } = transactionActions;

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale
);

const generateColors = (count) => {
  const baseColors = [
    "rgba(47, 165, 243, 0.6)", // soft blue
    "rgba(60, 198, 198, 0.7)", // teal
    "rgba(249, 80, 117, 0.6)", // soft red
    "rgba(255, 206, 86, 0.6)", // yellow
    "rgba(153, 102, 255, 0.6)", // violet
    "rgba(255, 159, 64, 0.6)", // orange
    "rgba(201, 203, 207, 0.5)", // gray
    "rgba(100, 181, 246, 0.6)", // light blue
    "rgba(129, 199, 132, 0.6)", // light green
    "rgba(244, 143, 177, 0.6)", // light pink
    "rgba(253, 122, 82, 0.6)", // salmon
    "rgba(174, 213, 129, 0.6)", // light olive
    "rgba(144, 164, 174, 0.6)", // slate gray
  ];
  return Array.from(
    { length: count },
    (_, i) => baseColors[i % baseColors.length]
  );
};

// 🔷 Payment Mode Chart
export const PaymentModeChart = ({ selectionFilter, setSelectionFilter }) => {
  const dispatch = useDispatch();
  const { getPieChartData } = transactionActions;
  const { agentId } = useSelector((state) => state?.authReducer);
  const { pieChartData } = useSelector((state) => state?.transactionReducer);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      { data: [], backgroundColor: [], totalTransactions: [], totalAmount: [] },
    ],
  });

  useEffect(() => {
    const paymentModeStats = Array.isArray(pieChartData?.data?.paymentModeStats)
      ? pieChartData?.data?.paymentModeStats
      : [];

    if (paymentModeStats.length > 0) {
      const labels = paymentModeStats.map(
        (item) => item?.paymentMode ?? "Unknown"
      );
      const amountData = paymentModeStats.map(
        (item) => item?.amountPercentage ?? 0
      );
      const txnData = paymentModeStats.map(
        (item) => item?.transactionPercentage ?? 0
      );
      const amounts = paymentModeStats.map((item) => item?.totalAmount / 100);
      const txns = paymentModeStats.map((item) => item?.totalTransactions);

      setChartData({
        labels,
        datasets: [
          {
            data: selectionFilter === "Amount" ? amountData : txnData,
            backgroundColor: generateColors(labels.length),
            totalAmount: amounts,
            totalTransactions: txns,
          },
        ],
      });
    }
  }, [pieChartData?.data, selectionFilter]);

  useEffect(() => {
    dispatch(getPieChartData(agentId || ""));
  }, [dispatch,agentId]);

  const downloadChart = () => {
    const link = document.createElement("a");
    link.download = "payment_mode_chart.png";
    link.href = document.querySelector("#paymentChart canvas").toDataURL();
    link.click();
  };

  return (
    <div className="chart-card payment-mode-card">
      <div className="chart-header">
        <h4 className="chart-title">Payment Mode Distribution</h4>
        <Space className="chart-controls">
          <Select
            value={selectionFilter}
            style={{ width: 160 }}
            onChange={setSelectionFilter}
            className="chart-select"
          >
            <Option value="Amount">Amount</Option>
            <Option value="Transactions">Transactions</Option>
          </Select>
          <Button
            onClick={downloadChart}
            className="download-btn"
            icon={<DownloadOutlined />}
          >
            Export
          </Button>
        </Space>
      </div>
      
      {pieChartData?.loading ? (
        <div className="chart-loading">
          <Spin />
        </div>
      ) : (
        <div id="paymentChart">
          <Doughnut
            data={chartData}
            options={{
              responsive: true,
              cutout: "70%",
              plugins: {
                title: {
                  display: true,
                  text: `Distribution by ${selectionFilter}`,
                  font: { size: 16 },
                },
                tooltip: {
                  callbacks: {
                    label: (tooltipItem) => {
                      const dataset = chartData.datasets[0];
                      const label = tooltipItem.label;
                      const raw = tooltipItem.raw?.toFixed(2);
                      const idx = tooltipItem.dataIndex;
                      const amt = dataset.totalAmount[idx]?.toLocaleString(
                        "en-IN",
                        {
                          style: "currency",
                          currency: "INR",
                        }
                      );
                      const txn = dataset.totalTransactions[idx];
                      return selectionFilter === "Amount"
                        ? `${label}: ${amt} (${raw}%)`
                        : `${label}: ${txn} Txns (${raw}%)`;
                    },
                  },
                },
                legend: {
                  position: "bottom",
                  labels: {
                    usePointStyle: true,
                    font: { size: 12 },
                    padding: 15,
                  },
                },
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

// 🔷 Transaction Status Chart
export const TransactionStatusChart = () => {
  const { pieChartData } = useSelector((state) => state.transactionReducer);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [{ data: [], backgroundColor: [] }],
  });

  useEffect(() => {
    const statusStats = Array.isArray(pieChartData?.data?.statusStats)
      ? pieChartData?.data?.statusStats
      : [];

    if (statusStats.length > 0) {
      const labels = statusStats.map((item) => item?.status ?? "Unknown");
      const data = statusStats.map((item) => item?.percentage ?? 0);
      const bgColor = labels.map((status) => {
        switch (status) {
          case "Successful":
            return "rgba(172, 219, 118, 0.6)";
          case "Pending":
            return "rgba(251, 119, 79, 0.6)";
          case "Failed":
            return " rgba(253, 51, 95, 0.6)";
          default:
            return "#007bff";
        }
      });

      setChartData({ labels, datasets: [{ data, backgroundColor: bgColor }] });
    }
  }, [pieChartData?.data]);

  return (
    <div className="chart-card transaction-status-card">
      <h5 style={{ textAlign: "center", paddingTop: "10px" }}>
        Transaction Rate
      </h5>
      <div
        id="txnStatusChart"
        style={{
          width: "240px",
          height: "240px",
          paddingLeft: "16px",
          margin: "10px auto",
        }}
      >
        <Doughnut
          data={chartData}
          options={{
            cutout: "65%",
            responsive: true,
            plugins: {
              tooltip: {
                callbacks: {
                  label: (tooltipItem) =>
                    `${tooltipItem.label}: ${tooltipItem.raw.toFixed(2)}%`,
                },
              },
              legend: {
                position: "bottom",
                labels: {
                  usePointStyle: true,
                  font: { size: 12 },
                  padding: 15,
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
};


export const ChannelsStatusChart = () => {
  const { transactionData } = useSelector((state) => ({
    transactionData: state.transactionReducer.transactionData,
  }));

  // Safe data access with null checks
  const aiCommission = transactionData?.data?.commission?.aiCommission || 0;
  const bankCommission = transactionData?.data?.commission?.bankCommission || 0;

  const channelSuccessData = {
    agt: {
      label: "AI Commission",
      success: Math.round(aiCommission),
    },
    mob: {
      label: "Bank Commission",
      success: Math.round(bankCommission),
    },
  };

  const chartData = {
    labels: Object.values(channelSuccessData).map((ch) => ch.label),
    datasets: [
      {
        data: Object.values(channelSuccessData).map((ch) => ch.success),
        backgroundColor: ["#FFB6C1", "#DDA0DD", "#87CEFA"],
        borderWidth: 1,
      },
    ],
  };

  // Show loading state if data isn't available

  return (
    <div className="chart-card transaction-status-card channel">
      <h5 style={{ paddingTop: "10px" }}>Commission Distribution</h5>
      <div
        id="channelsChart"
        style={{ width: "240px", height: "240px", margin: "0 auto" }}
      >
        <Doughnut
          data={chartData}
          options={{
            responsive: true,
            cutout: "65%",
            plugins: {
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const label = context.label;
                    const value = context.raw;
                    const total = context.dataset.data.reduce(
                      (a, b) => a + b,
                      0
                    );
                    const percentage = ((value / total) * 100).toFixed(1);
                    return `${label}: ₹${value} (${percentage}%)`;
                  },
                },
              },
              legend: {
                position: "bottom",
                labels: {
                  usePointStyle: true,
                  font: { size: 12 },
                  padding: 15,
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
};
